import { enqueueSnackbar } from 'notistack';
import {
  useRemoveDatabaseEntities,
  useUpdateDatabaseEntitiesStatus,
} from 'api/bitedb/entities/entities.hooks';
import { isStatusFlag } from 'api/bitedb/entities/statusFlags';

export type EntitySelectionAction = 'delete' | 'updateStatus';

/**
 * Remove from Database and selection status for selected Database Entities of
 * the team, which may span several of its Databases (Review Queue, roll-ups,
 * Entities Overview). Each action is one request; the outcome is reported in a
 * snackbar. `onSettled` runs after an action was attempted, whatever its
 * outcome, and not when nothing is selected.
 */
export const useEntitySelectionActions = (selectedTeamId?: string) => {
  const removeDatabaseEntitiesMutation = useRemoveDatabaseEntities(selectedTeamId);
  const updateDatabaseEntitiesStatusMutation =
    useUpdateDatabaseEntitiesStatus(selectedTeamId);

  const runSelectionAction = async (
    selectedItems: Array<{ association_id?: unknown }>,
    action: EntitySelectionAction,
    status: string | undefined,
    onSettled: () => void,
  ): Promise<void> => {
    const databaseEntityIds = selectedItems
      .map((item) => item.association_id)
      .filter(Boolean)
      .map(String);

    if (databaseEntityIds.length === 0) {
      enqueueSnackbar('Please select at least one entity', {
        variant: 'error',
      });
      return;
    }

    const actionPastTense =
      action === 'delete' ? 'removed from the Database' : 'updated';

    try {
      if (action === 'delete') {
        await removeDatabaseEntitiesMutation.mutateAsync(databaseEntityIds);
      } else if (isStatusFlag(status)) {
        await updateDatabaseEntitiesStatusMutation.mutateAsync({
          databaseEntityIds: databaseEntityIds,
          toStatus: status,
        });
      } else {
        throw new Error('Please select a status');
      }

      enqueueSnackbar(`Entities ${actionPastTense} successfully`, {
        variant: 'success',
      });
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || `Entities could not be ${actionPastTense}`,
        { variant: 'error' },
      );
    } finally {
      onSettled();
    }
  };

  return {
    removeDatabaseEntitiesMutation: removeDatabaseEntitiesMutation,
    updateDatabaseEntitiesStatusMutation: updateDatabaseEntitiesStatusMutation,
    runSelectionAction: runSelectionAction,
  };
};
